import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';

import confirmRegistration from 'actions/cognito/confirmRegistration.action';
import resendConfirmationCode from 'actions/cognito/resendConfirmationCode.action';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog.component';
import OldCodeInput from 'components/form/OldCodeInput.component';
import Spinner from 'components/generic/Spinner.component';
import LINK from 'constants/link.const';
import S from 'constants/string.const';
import { useNavFor } from 'hooks/useNav.hook';
import { useTranslation } from 'providers/GILT.provider';
import asArray from 'utils/arrays/asArray.util';
import { xeLogoutUser } from 'utils/cognito.util';
import { fromSearchToQueryString } from 'utils/converters/props/path.util';
import logger from 'utils/logger.util';

import OnboardingFooter from 'onboarding/common/OnboardingFooter';
import Header from 'user/common/Header';

import CN from './CodeVerification.module.scss';

const CTAG = 'CodeVerification';
const L = logger(CTAG);

const LENGTH = 6;
const SPINNER_MESSAGE = '';
const SPINNER_COLOR = 'white';

// NOTE: logout/login is a shortcut for refreshing the user Cognito state after confirmation
const FLOW_SHOULD_GO_TO_LOGIN = true;

// must be 6 digits from 0 to 9 and nothing else
const isValidSmsCode = (array) => /^[0-9]{6}$/.test(asArray(array).join(''));
const fromErrorToMessage = ({ code, message }, t) => (code === 'UserNotFoundException' ? t('codeVerification.userNotFound') : message);
const nullArray = (length) => Array(length).fill(null);

const CodeVerification = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const search = fromSearchToQueryString(location?.search);
  const nav = useNavFor(CTAG);
  const { t } = useTranslation();

  const [code, setCode] = useState(nullArray(LENGTH));
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [checkmarks, setCheckmarks] = useState('');

  const handleValidate = useCallback((value) => {
    if (asArray(value).includes(null)) {
      // not finished typing yet
      setIsButtonEnabled(false);
      return '';
    }

    if (isValidSmsCode(value)) {
      setIsButtonEnabled(true);
      return '';
    }

    setIsButtonEnabled(false);
    return L.info('handleValidate()', 'invalid value:', value, 'returning message:', 'Please enter a valid code');
  }, []);

  const handleRequestNew = useCallback(() => {
    setIsLoading(true);
    L.unignore('()', 'handleRequestNew()', 'resendConfirmationCode:', dispatch(resendConfirmationCode()))
      .then(() => {
        setIsButtonEnabled(false);
        setErrorMessage('');
        setCode(nullArray(LENGTH));
        setCheckmarks(checkmarks.length < 3 ? checkmarks + S.checkmark : S.checkmark);
      })
      .catch((e) => setErrorMessage(fromErrorToMessage(e, t)))
      .finally(() => setIsLoading(false));
  }, [checkmarks, dispatch, t]);

  const handleNext = useCallback(() => {
    const codeString = code.join('');

    L.debug('()', 'handleNext()', { codeString, code });

    if (!isButtonEnabled) {
      return;
    }

    setIsLoading(true);
    dispatch(confirmRegistration({ code: codeString }))
      .then(() => {
        setIsButtonEnabled(false);
        setErrorMessage('');

        if (FLOW_SHOULD_GO_TO_LOGIN) {
          setIsSuccess(true);
        } else {
          nav.push({ pathname: LINK.onboardingType, search });
        }
      })
      .catch((e) => setErrorMessage(fromErrorToMessage(e, t)))
      .finally(() => setIsLoading(false));
  }, [code, isButtonEnabled, dispatch, nav, search, t]);

  const handleLogout = useCallback(() => void dispatch(xeLogoutUser()), [dispatch]);

  return (
    <div data-bem="CodeVerification">
      <div data-bem="CodeVerification__content" className="content-wrap reset-link with-content">
        <Header title={t('codeVerification.title')} description={t('codeVerification.subtitle')} />

        {isSuccess && (
          <ConfirmationDialog title={t('codeVerification.confirmationModal.title')} rightButtonText={t('common.buttons.ok')} onRight={handleLogout}>
            <p>
              {t('codeVerification.confirmationModal.subtitle')}
              <br />
              <br />
              {t('codeVerification.confirmationModal.description')}
            </p>
          </ConfirmationDialog>
        )}

        <div data-bem="CodeVerification__code" className="content login auto-height">
          <OldCodeInput id="CodeInput" label={t('codeVerification.codeLabel')} type="text" inputValue="" onBlur={setCode} onValidate={handleValidate} />

          {isLoading && <Spinner color={SPINNER_COLOR} message={SPINNER_MESSAGE} />}

          <div data-bem="CodeVerification__resend" className={CN.center}>
            <button className="btn btn-violet-link" onClick={handleRequestNew} tabIndex="0">
              {t('codeVerification.reSendButton')} {checkmarks}
            </button>
          </div>

          <div
            data-bem="CodeVerification__error"
            className={cn({
              'input-prompt-show': errorMessage,
              'input-prompt-hide': !errorMessage,
            })}
          >
            <span>{errorMessage}</span>
          </div>
        </div>

        <OnboardingFooter isFooterButtonActive={isButtonEnabled} onButtonClick={handleNext} isLoading={false} />
      </div>
    </div>
  );
};

export default CodeVerification;
